import { useState, useEffect, useRef } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

const DISMISS_KEY = 'careweave_install_dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if ((navigator as any).standalone === true) return true;
  if (document.referrer.includes('android-app://')) return true;
  return false;
}

function getIOSInfo() {
  const ua = navigator.userAgent;
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /safari/i.test(ua) && !/crios|fxios|opios|chrome|android/i.test(ua);
  return { isIOS, isSafari };
}

function wasDismissedRecently(): boolean {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    return Date.now() - Number(ts) < DISMISS_DURATION_MS;
  } catch { /* localStorage may be unavailable */
    return false;
  }
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSBanner, setShowIOSBanner] = useState(false);
  const [visible, setVisible] = useState(false);
  const promptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    // Android / Chrome install prompt
    const handler = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      promptRef.current = e;
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari fallback
    const { isIOS, isSafari } = getIOSInfo();
    if (isIOS && isSafari) {
      setShowIOSBanner(true);
      setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    const prompt = promptRef.current;
    if (!prompt) return;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
    }
    setDeferredPrompt(null);
    promptRef.current = null;
  };

  const handleDismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch { /* localStorage may be unavailable */ }
  };

  if (!visible) return null;

  // iOS Safari — show share instructions
  if (showIOSBanner && !deferredPrompt) {
    return (
      <div style={{
        position: 'fixed',
        bottom: 72,
        left: 12,
        right: 12,
        zIndex: 400,
        background: 'var(--color-accent)',
        borderRadius: 'var(--radius-lg)',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        boxShadow: 'var(--shadow-overlay)',
        border: '1px solid var(--color-border)',
        animation: 'slideUp .35s ease-out',
      }}>
        <span style={{ fontSize: 'var(--fs-2xl)', flexShrink: 0, marginTop: 2 }}>📲</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 'var(--fs-sm-plus)', fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
            Add CareWeave to your Home Screen
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-xs-plus)', color: 'rgba(255,255,255,0.92)', lineHeight: 1.4 }}>
            Tap the <strong style={{ color: '#fff' }}>Share</strong> button
            <span style={{ display: 'inline-block', margin: '0 4px', fontSize: 'var(--fs-base)', verticalAlign: 'middle' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/>
                <polyline points="16 6 12 2 8 6"/>
                <line x1="12" y1="2" x2="12" y2="15"/>
              </svg>
            </span>
            then <strong style={{ color: '#fff' }}>Add to Home Screen</strong> to open CareWeave like an app.
          </p>
        </div>
        {/* eslint-disable-next-line cw/no-inline-button-style -- InstallPrompt iOS-Share dismiss × — round 4-10 padding pill on dark gradient header */}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss install prompt"
          style={{
            background: 'color-mix(in srgb, var(--color-text-primary) 6%, transparent)',
            border: 'none',
            borderRadius: 'var(--radius-xl)',
            color: '#fff',
            fontSize: 'var(--fs-sm)',
            fontWeight: 600,
            cursor: 'pointer',
            padding: '4px 10px',
            flexShrink: 0,
            marginTop: 2,
          }}
        >
          ✕
        </button>
      </div>
    );
  }

  // Android / Chrome / Edge — show install button
  if (deferredPrompt) {
    return (
      <div style={{
        position: 'fixed',
        bottom: 72,
        left: 12,
        right: 12,
        zIndex: 400,
        background: 'var(--color-accent)',
        borderRadius: 'var(--radius-lg)',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        boxShadow: 'var(--shadow-overlay)',
        border: '1px solid var(--color-border)',
        animation: 'slideUp .35s ease-out',
      }}>
        <span style={{ fontSize: 'var(--fs-2xl)', flexShrink: 0 }}>📲</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 'var(--fs-sm-plus)', fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
            Install CareWeave
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 'var(--fs-xs-plus)', color: 'rgba(255,255,255,0.92)', lineHeight: 1.3 }}>
            Add to your Home Screen for quick access — works offline too!
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {/* eslint-disable-next-line cw/no-inline-button-style -- InstallPrompt Later — small pill in install-banner footer pair */}
          <button
            onClick={handleDismiss}
            aria-label="Dismiss install prompt"
            style={{
              background: 'color-mix(in srgb, var(--color-text-primary) 6%, transparent)',
              border: 'none',
              borderRadius: 'var(--radius-xl)',
              color: '#fff',
              fontSize: 'var(--fs-xs-plus)',
              fontWeight: 600,
              cursor: 'pointer',
              padding: '6px 12px',
            }}
          >
            Later
          </button>
          {/* eslint-disable-next-line cw/no-inline-button-style -- InstallPrompt Install CTA — accent-gradient pill in install banner, parked gradient family */}
          <button
            onClick={handleInstallClick}
            style={{
              background: 'var(--color-accent)',
              border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)',
              borderRadius: 'var(--radius-xl)',
              color: '#fff',
              fontSize: 'var(--fs-xs-plus)',
              fontWeight: 700,
              cursor: 'pointer',
              padding: '6px 14px',
              whiteSpace: 'nowrap',
            }}
          >
            Install
          </button>
        </div>
      </div>
    );
  }

  return null;
}
